import React, { useState, useMemo, useEffect } from 'react';
import { useProducts } from '../context/ProductsContext';
import { useMedia } from '../context/MediaContext';
import { useStock } from '../context/StockContext';
import { pickProductMediaUrl, pickVariantImageUrl } from '../utils/media';
import { cloudinaryUrl } from '../utils/cloudinary';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { faStar, faArrowDownWideShort, faFilter, faXmark, faArrowLeft, faArrowRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getShopState, setShopState } from '../context/shopState';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import type { Product } from '../types/product-variant';


type FilterState = {
  sortBy: string;
  category: string;
  collection: string;
  showOneOfKind: boolean | null;
  inStockOnly: boolean;
  search: string;
};

const ITEMS_PER_PAGE = 12;

// Add this helper function at the top of the file, before the Shop component
const mapCategory = (category: string): string => {
  // Normalize the category string
  const normalized = category.toLowerCase().trim();
  
  // Handle earring variations
  if (normalized.includes('earring') || normalized.includes('hoop')) {
    return 'Earrings and Hoops';
  }
  
  // Handle ring variations
  if (normalized.includes('ring')) {
    return 'Rings';
  }
  
  // Group all other categories under "Other"
  return 'Other';
};

const Shop: React.FC = () => {
  const { products, loading: productsLoading } = useProducts();
  const { getMediaByProduct, loading: mediaLoading } = useMedia();
  const { getStock } = useStock();
  const [searchParams] = useSearchParams();

  // Initialize state from URL params or stored values
  const [filters, setFilters] = useState<FilterState>(() => {
    const categoryParam = searchParams.get('category');
    const collectionParam = searchParams.get('collection');
    const oneOfKindParam = searchParams.get('oneOfKind');
    const inStockParam = searchParams.get('inStock');
    const searchParam = searchParams.get('search');

    return {
      sortBy: getShopState().filters.sortBy,
      category: categoryParam || getShopState().filters.category,
      collection: collectionParam || getShopState().filters.collection,
      showOneOfKind: oneOfKindParam ? oneOfKindParam === 'true' : getShopState().filters.showOneOfKind,
      inStockOnly: inStockParam ? inStockParam === 'true' : getShopState().filters.inStockOnly,
      search: searchParam || ''
    };
  });

  // A product is immediately available if it (or at least one of its variants) has stock on hand.
  // Made-to-order / backorder items have stock 0 and are excluded by this check.
  const isAvailableNow = React.useCallback((product: Product): boolean => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.some(variant => getStock(product.id, variant.id) > 0);
    }
    return getStock(product.id) > 0;
  }, [getStock]);
  const [currentPage, setCurrentPage] = useState(getShopState().currentPage);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useTranslation();
  const { localePath } = useLocale();

  const pickMediaUrl = (productId: string | undefined, productSlug: string | undefined, usage: string, fallbackHints: string[]) => {
    // Don't query media until both are loaded
    if (!productId && !productSlug) return undefined;
    if (mediaLoading || productsLoading) return undefined;

    let media = productId ? getMediaByProduct(productId) : [];
    if (media.length === 0 && productSlug) {
      media = getMediaByProduct(productSlug);
    }

    return pickProductMediaUrl(media, fallbackHints, usage);
  };

  const logMediaPick = (product: { id: string; airtableId?: string; name: string; slug: string }) => {
    if (!import.meta.env.DEV) return;

    let media = product.airtableId ? getMediaByProduct(product.airtableId) : getMediaByProduct(product.id);
    if (media.length === 0) {
      media = getMediaByProduct(product.slug);
    }
    const pickedMain = pickProductMediaUrl(media, ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal'], 'product-main');
    const pickedSecondary = pickProductMediaUrl(media, ['-secondary', 'secondary', '-model', 'model', '-modelo', 'modelo'], 'product-secondary');
    const pickedThird = pickProductMediaUrl(media, ['-third', 'third', '-zoom', 'zoom'], 'product-third');

    console.debug('[media] shop card', {
      id: product.id,
      name: product.name,
      airtableId: product.airtableId,
      mediaCount: media.length,
      mediaIds: media.map(m => m.id),
      picked: { main: pickedMain, secondary: pickedSecondary, third: pickedThird },
    });
  };

  // Add this near the top of your Shop component to see what categories exist
  useEffect(() => {
    console.log('All categories:', [...new Set(products.map(p => p.category))]);
    console.log('Mapped categories:', [...new Set(products.map(p => mapCategory(p.category)))]);
  }, []);

  // Get filtered products without sorting
  const getFilteredProducts = (currentFilters: FilterState) => {
    return products.filter(product => {
      const mappedCategory = mapCategory(product.category);
      
      // Handle category filtering
      if (currentFilters.category !== 'all' && mappedCategory !== currentFilters.category) {
        return false;
      }

      // Collection filter
      if (currentFilters.collection !== 'all' && product.collection !== currentFilters.collection) {
        return false;
      }

      // One of Kind filter
      if (currentFilters.showOneOfKind !== null && product.oneOfKind !== currentFilters.showOneOfKind) {
        return false;
      }

      // In Stock Now filter (excludes made-to-order / backorder items)
      if (currentFilters.inStockOnly && !isAvailableNow(product)) {
        return false;
      }

      // Search filter
      if (currentFilters.search) {
        const searchTerm = currentFilters.search.toLowerCase();
        const productName = product.name.toLowerCase();
        const productCollection = (product.collection || '').toLowerCase();
        const productCategory = product.category.toLowerCase();
        
        return productName.includes(searchTerm) || 
               productCollection.includes(searchTerm) ||
               productCategory.includes(searchTerm);
      }

      return true;
    });
  };

  // Get available options based on current filters
  const getAvailableOptions = () => {
    const tempFilters = { ...filters };
    const forCategories = getFilteredProducts({ ...tempFilters, category: 'all' });
    
    // Define the simplified categories
    const mainCategories = ["Earrings and Hoops", "Rings", "Other"];
    const availableCategories = mainCategories.filter(category =>
      forCategories.some(product => mapCategory(product.category) === category)
    );

    // Check if there are any products that don't fit in main categories
    const otherItems = forCategories.filter(product => {
      const mappedCategory = mapCategory(product.category);
      return !mainCategories.includes(mappedCategory);
    });

    if (otherItems.length > 0) {
      availableCategories.push("Other");
    }

    // Get collections
    const forCollections = getFilteredProducts({ ...tempFilters, collection: 'all' });
    const availableCollections = [...new Set(forCollections.map(p => p.collection))];

    // Check one-of-kind status
    const forOneOfKind = getFilteredProducts({ ...tempFilters, showOneOfKind: null });
    const hasOneOfKind = forOneOfKind.some(p => p.oneOfKind);
    const hasLimitedSeries = forOneOfKind.some(p => !p.oneOfKind);

    return {
      categories: availableCategories,
      collections: availableCollections,
      hasOneOfKind,
      hasLimitedSeries
    };
  };

  const { categories, collections, hasOneOfKind, hasLimitedSeries } = getAvailableOptions();

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const mappedCategory = mapCategory(product.category);
      
      // Category filter
      if (filters.category !== 'all' && mappedCategory !== filters.category) {
        return false;
      }

      // Collection filter
      if (filters.collection !== 'all' && product.collection !== filters.collection) {
        return false;
      }

      // One of Kind filter
      if (filters.showOneOfKind !== null && product.oneOfKind !== filters.showOneOfKind) {
        return false;
      }

      // In Stock Now filter (excludes made-to-order / backorder items)
      if (filters.inStockOnly && !isAvailableNow(product)) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const productName = product.name.toLowerCase();
        const productCollection = (product.collection || '').toLowerCase();
        const productCategory = product.category.toLowerCase();
        
        return productName.includes(searchTerm) || 
               productCollection.includes(searchTerm) ||
               productCategory.includes(searchTerm);
      }

      return true;
    });

    // Sort products
    switch (filters.sortBy) {
      case 'collection-asc':
        return filtered.sort((a, b) => 
          (a.collection || '').localeCompare(b.collection || '')
        );
      case 'collection-desc':
        return filtered.sort((a, b) => 
          (b.collection || '').localeCompare(a.collection || '')
        );
      case 'price-asc':
        return filtered.sort((a, b) => {
          // Get minimum price for products with variants
          const priceA = a.variants 
            ? Math.min(...a.variants.map(v => v.price))
            : a.price || 0;
          const priceB = b.variants 
            ? Math.min(...b.variants.map(v => v.price))
            : b.price || 0;
          return priceA - priceB;
        });
      case 'price-desc':
        return filtered.sort((a, b) => {
          // Get maximum price for products with variants
          const priceA = a.variants 
            ? Math.max(...a.variants.map(v => v.price))
            : a.price || 0;
          const priceB = b.variants 
            ? Math.max(...b.variants.map(v => v.price))
            : b.price || 0;
          return priceB - priceA;
        });
      case 'alpha-asc':
        return filtered.sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
      case 'alpha-desc':
        return filtered.sort((a, b) => 
          (b.name || '').localeCompare(a.name || '')
        );
      case 'newest':
      default:
        return filtered.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
    }
    return filtered;
  }, [filters, products, isAvailableNow]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Smooth scroll to top of products grid
    document.querySelector('.products-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Add useEffect for body scroll lock
  useEffect(() => {
    if (isMobileFiltersOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileFiltersOpen]);

  // Add effect to save state
  useEffect(() => {
    setShopState({
      filters,
      currentPage,
      scrollPosition: window.scrollY
    });
  }, [filters, currentPage]);

  // Restore scroll position on mount
  useEffect(() => {
    const savedPosition = getShopState().scrollPosition;
    if (savedPosition) {
      window.scrollTo(0, savedPosition);
    }
  }, []);

  // Add this function near your other handlers
  const clearFilters = () => {
    setFilters({
      sortBy: 'newest',
      category: 'all',
      collection: 'all',
      showOneOfKind: null,
      inStockOnly: false,
      search: ''
    });
    setCurrentPage(1);
  };

  return (
    <>
      <SEO
        title={t('seo.shop.title')}
        description={t('seo.shop.description')}
      />
      <div className="container mx-auto px-4 py-8 max-w-[min(90vw,1400px)]">
        {/* Hero Section */}
        <div className="flex justify-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-black text-center dark:text-white p-4">
            {t('shop.discoverCollections')}
          </h1>
        </div>

        {/* Mobile Filter and Search Buttons */}
        <div className="lg:hidden mb-6">
            <div className="flex items-center gap-3 mb-3">
                <button
                    onClick={() => setIsMobileFiltersOpen(true)}
                    className="flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-brand-blue text-white hover:bg-brand-blue/90 transition-colors"
                >
                    <FontAwesomeIcon icon={faFilter} />
                    <span className="font-medium">{t('shop.filters')}</span>
                </button>

                {/* Sort By — takes the remaining width since option labels vary in length */}
                <div className="flex-1 min-w-0 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-white/10 border border-brand-dark/15 dark:border-white/15">
                    <FontAwesomeIcon
                        icon={faArrowDownWideShort}
                        className="flex-shrink-0 text-brand-gray h-3.5"
                    />
                    <select
                        value={filters.sortBy}
                        onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                        className="flex-1 min-w-0 bg-transparent text-brand-dark dark:text-brand-white text-sm focus:outline-none cursor-pointer"
                    >
                        <option value="newest">{t('shop.sortNewest')}</option>
                        <option value="alpha-asc">{t('shop.sortAZ')}</option>
                        <option value="alpha-desc">{t('shop.sortZA')}</option>
                        <option value="collection-asc">{t('shop.sortCollectionAZ')}</option>
                        <option value="collection-desc">{t('shop.sortCollectionZA')}</option>
                        <option value="price-asc">{t('shop.sortPriceLow')}</option>
                        <option value="price-desc">{t('shop.sortPriceHigh')}</option>
                    </select>
                </div>

                <button
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                    className={`flex-shrink-0 p-2.5 rounded-lg border transition-colors ${
                        isSearchOpen || filters.search
                            ? 'border-brand-blue text-brand-blue bg-brand-blue/5'
                            : 'border-brand-dark/20 dark:border-white/20 text-brand-dark dark:text-brand-white'
                    }`}
                >
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                </button>

                {/* Clear All Button - Mobile */}
                {(filters.category !== 'all' ||
                  filters.collection !== 'all' ||
                  filters.showOneOfKind !== null ||
                  filters.inStockOnly ||
                  filters.search !== '') && (
                    <button
                        onClick={clearFilters}
                        className="flex-shrink-0 p-2.5 rounded-lg border border-brand-dark/20 dark:border-white/20 text-brand-dark dark:text-brand-white hover:border-brand-blue hover:text-brand-blue transition-colors"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>

            {/* Mobile Search Input — expands below the control row, pushes content down, no overlap */}
            <AnimatePresence>
                {isSearchOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden mb-3"
                    >
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t('shop.searchPlaceholder')}
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-white/10 border border-brand-dark/15 dark:border-white/15 text-brand-dark dark:text-brand-white placeholder-brand-gray focus:outline-none focus:border-brand-blue"
                                autoFocus
                            />
                            {filters.search && (
                                <button
                                    onClick={() => {
                                        setFilters({ ...filters, search: '' });
                                        setIsSearchOpen(false);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-brand-blue"
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>

        {/* Mobile Filters Modal */}
        <AnimatePresence>
          {isMobileFiltersOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              className="fixed inset-0 bg-brand-sand dark:bg-brand-charcoal z-50 md:hidden overflow-y-auto"
            >
              <div className="p-4">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-light tracking-wide text-brand-dark dark:text-brand-white">{t('shop.filters')}</h2>
                  <button
                    onClick={() => setIsMobileFiltersOpen(false)}
                    className="text-brand-dark dark:text-brand-white hover:text-brand-blue transition-colors"
                  >
                    <FontAwesomeIcon icon={faXmark} size="lg" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex flex-col gap-4">
                    <select
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white dark:bg-white/10 border border-brand-dark/10 dark:border-white/10 text-brand-dark dark:text-brand-white focus:outline-none focus:border-brand-blue"
                    >
                      <option value="all">{t('shop.allCategories')}</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{t(`categories.${category}`, category)}</option>
                      ))}
                    </select>

                    <select
                      value={filters.collection}
                      onChange={(e) => setFilters({ ...filters, collection: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white dark:bg-white/10 border border-brand-dark/10 dark:border-white/10 text-brand-dark dark:text-brand-white focus:outline-none focus:border-brand-blue"
                    >
                      <option value="all">{t('shop.allCollections')}</option>
                      {collections.map(collection => (
                        <option key={collection} value={collection}>{collection}</option>
                      ))}
                    </select>

                    <select
                      value={filters.showOneOfKind ? 'true' : filters.showOneOfKind === false ? 'false' : 'all'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters({
                          ...filters,
                          showOneOfKind: value === 'true' ? true : value === 'false' ? false : null
                        });
                      }}
                      className="w-full px-4 py-2 rounded-lg bg-white dark:bg-white/10 border border-brand-dark/10 dark:border-white/10 text-brand-dark dark:text-brand-white focus:outline-none focus:border-brand-blue"
                    >
                      <option value="all">{t('shop.allItems')}</option>
                      {hasOneOfKind && <option value="true">{t('shop.oneOfKind')}</option>}
                      {hasLimitedSeries && <option value="false">{t('shop.limitedSeries')}</option>}
                    </select>

                    <button
                      type="button"
                      onClick={() => setFilters({ ...filters, inStockOnly: !filters.inStockOnly })}
                      aria-pressed={filters.inStockOnly}
                      className={`w-full text-left px-4 py-2 rounded-lg border transition-colors ${
                        filters.inStockOnly
                          ? 'bg-brand-blue border-brand-blue text-white'
                          : 'bg-white dark:bg-white/10 border-brand-dark/10 dark:border-white/10 text-brand-dark dark:text-brand-white'
                      }`}
                    >
                      {t('shop.inStockOnly')}
                    </button>

                    {/* Clear Filters Button - Inside Modal */}
                    {(filters.category !== 'all' ||
                      filters.collection !== 'all' ||
                      filters.showOneOfKind !== null ||
                      filters.inStockOnly ||
                      filters.search !== '') && (
                      <button
                        onClick={clearFilters}
                        className="w-full px-4 py-3 rounded-lg border border-brand-dark/20 dark:border-white/20 text-brand-dark dark:text-brand-white hover:border-brand-blue hover:text-brand-blue transition-colors font-medium"
                      >
                        {t('shop.clearAll')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Mobile Filters Modal bottom buttons */}
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-brand-sand dark:bg-brand-charcoal border-t border-brand-dark/10 dark:border-white/10">
                  <div className="flex gap-4">
                    {(filters.category !== 'all' || 
                      filters.collection !== 'all' || 
                      filters.showOneOfKind !== null ||
                      filters.inStockOnly ||
                      filters.search !== '') && (
                      <>
                        <button
                          onClick={clearFilters}
                          className="w-1/2 px-4 py-3 border border-brand-blue text-brand-blue rounded-lg hover:bg-brand-blue/10 transition-colors"
                        >
                          {t('shop.clearFilters')}
                        </button>
                        <button
                          onClick={() => setIsMobileFiltersOpen(false)}
                          className="w-1/2 px-4 py-3 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors"
                        >
                          {t('shop.applyFilters')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Filters */}
        <div className="hidden lg:block mb-8 relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] px-4">
            <div className="mx-auto max-w-[1200px] flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
                {/* Left: Main Filters */}
                <div className="flex items-center gap-3">
                    <select
                        value={filters.category}
                        onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                        className="px-4 py-2 rounded-lg bg-white dark:bg-brand-charcoal border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:border-brand-blue focus:outline-none focus:border-brand-blue transition-colors cursor-pointer"
                    >
                        <option value="all">{t('shop.allCategories')}</option>
                        {categories.map(category => (
                            <option key={category} value={category}>{t(`categories.${category}`, category)}</option>
                        ))}
                    </select>

                    <select
                        value={filters.collection}
                        onChange={(e) => setFilters({ ...filters, collection: e.target.value })}
                        className="px-4 py-2 rounded-lg bg-white dark:bg-brand-charcoal border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:border-brand-blue focus:outline-none focus:border-brand-blue transition-colors cursor-pointer"
                    >
                        <option value="all">{t('shop.allCollections')}</option>
                        {collections.map(collection => (
                            <option key={collection} value={collection}>{collection}</option>
                        ))}
                    </select>

                    <select
                        value={filters.showOneOfKind ? 'true' : filters.showOneOfKind === false ? 'false' : 'all'}
                        onChange={(e) => {
                            const value = e.target.value;
                            setFilters({ 
                                ...filters, 
                                showOneOfKind: value === 'true' ? true : value === 'false' ? false : null 
                            });
                        }}
                        className="px-4 py-2 rounded-lg bg-white dark:bg-brand-charcoal border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:border-brand-blue focus:outline-none focus:border-brand-blue transition-colors cursor-pointer"
                    >
                        <option value="all">{t('shop.allItems')}</option>
                        {hasOneOfKind && <option value="true">{t('shop.oneOfKind')}</option>}
                        {hasLimitedSeries && <option value="false">{t('shop.limitedSeries')}</option>}
                    </select>

                    <button
                        type="button"
                        onClick={() => setFilters({ ...filters, inStockOnly: !filters.inStockOnly })}
                        aria-pressed={filters.inStockOnly}
                        className={`px-4 py-2 rounded-lg border transition-colors whitespace-nowrap ${
                            filters.inStockOnly
                                ? 'bg-brand-blue border-brand-blue text-white'
                                : 'bg-white dark:bg-brand-charcoal border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:border-brand-blue'
                        }`}
                    >
                        {t('shop.inStockOnly')}
                    </button>
                </div>

                {/* Sort, Search, and Clear */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-brand-charcoal border border-gray-300 dark:border-gray-600">
                        <FontAwesomeIcon 
                            icon={faArrowDownWideShort} 
                            className="text-gray-500 dark:text-gray-400 h-3.5"
                        />
                        <select
                            value={filters.sortBy}
                            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                            className="bg-transparent text-gray-900 dark:text-white focus:outline-none cursor-pointer border-none"
                        >
                            <option value="newest">{t('shop.sortNewest')}</option>
                            <option value="alpha-asc">{t('shop.sortAZ')}</option>
                            <option value="alpha-desc">{t('shop.sortZA')}</option>
                            <option value="collection-asc">{t('shop.sortCollectionAZ')}</option>
                            <option value="collection-desc">{t('shop.sortCollectionZA')}</option>
                            <option value="price-asc">{t('shop.sortPriceLow')}</option>
                            <option value="price-desc">{t('shop.sortPriceHigh')}</option>
                        </select>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setIsSearchOpen(!isSearchOpen)}
                            className={`p-2 rounded-lg border transition-colors ${
                                isSearchOpen || filters.search 
                                    ? 'border-brand-blue text-brand-blue bg-brand-blue/5' 
                                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-brand-blue hover:text-brand-blue'
                            }`}
                        >
                            <FontAwesomeIcon icon={faMagnifyingGlass} className="h-4" />
                        </button>
                        <AnimatePresence>
                            {isSearchOpen && (
                                <motion.div
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.95, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute right-0 top-full mt-2 z-10 w-80"
                                >
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder={t('shop.searchPlaceholder')}
                                            value={filters.search}
                                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                            className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-brand-charcoal border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-brand-blue shadow-lg"
                                            autoFocus
                                        />
                                        {filters.search && (
                                            <button
                                                onClick={() => {
                                                    setFilters({ ...filters, search: '' });
                                                    setIsSearchOpen(false);
                                                }}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-brand-blue"
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Clear Filters Button */}
                    {(filters.category !== 'all' || 
                      filters.collection !== 'all' || 
                      filters.showOneOfKind !== null ||
                      filters.inStockOnly ||
                      filters.search !== '') && (
                        <button
                            onClick={clearFilters}
                            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-brand-blue hover:text-brand-blue transition-colors whitespace-nowrap"
                        >
                            {t('shop.clearAllShort')}
                        </button>
                    )}
                </div>
            </div>
        </div>

        {/* Products Grid */}
        <div className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] px-0 md:px-4">
          <div className="container mx-auto">
            {/* Add products count */}
            <p className="text-dark dark:text-white mb-4">
              {t('shop.showing', { current: currentProducts.length, total: filteredProducts.length })}
            </p>

            <div className="products-grid grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px md:gap-8">
              {(productsLoading || mediaLoading) ? (
                <div className="col-span-4 text-center py-12 text-lg text-gray-500 dark:text-gray-300">
                  {t('shop.loading')}
                </div>
              ) : (
                currentProducts.map(product => (
                  <motion.div
                    key={product.id}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white dark:bg-brand-charcoal rounded-none md:rounded-xl shadow-none md:shadow-md overflow-hidden"
                  >
                    {import.meta.env.DEV ? (logMediaPick(product), null) : null}
                    <Link to={localePath('/product/:slug', { slug: product.slug })}>
                      <div className="relative">
                        <img
                          src={cloudinaryUrl(
                            pickMediaUrl(
                              product.airtableId || product.id,
                              product.slug,
                              'product-main',
                              ['-main', ' main', 'main-', '_main', '-primary', 'primary', 'front', 'principal']
                            ) ||
                            pickVariantImageUrl(product.variants?.[0]?.images, 0) ||
                            pickMediaUrl(
                              product.airtableId || product.id,
                              product.slug,
                              'product-secondary',
                              ['-secondary', 'secondary', '-model', 'model', '-modelo', 'modelo']
                            ) ||
                            pickVariantImageUrl(product.variants?.[0]?.images, 1) ||
                            pickMediaUrl(
                              product.airtableId || product.id,
                              product.slug,
                              'product-third',
                              ['-third', 'third', '-zoom', 'zoom']
                            ) ||
                            pickVariantImageUrl(product.variants?.[0]?.images, 2) ||
                            '',
                            400
                          )}
                          alt={product.name}
                          className="w-full aspect-square md:aspect-auto md:h-64 object-cover"
                          loading="lazy"
                        />
                        {product.oneOfKind && (
                          <div className="absolute top-2 right-2 bg-brand-blue text-white text-xs px-2 py-1 rounded">
                            {t('product.oneOfKindBadge')}
                          </div>
                        )}
                        {product.variants && product.variants.length > 0 && (
                          <div className="absolute top-2 left-2 bg-brand-blue/80 text-white text-xs px-2 py-1 rounded">
                            {t('product.multipleOptions')}
                          </div>
                        )}
                        {/* Mobile overlay text */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 md:hidden">
                          <h3 className="text-sm font-medium text-white truncate">{product.name}</h3>
                          <p className="text-xs text-white/90">
                            {product.variants && product.variants.length > 0
                              ? `${t('product.from')} €${Math.min(...product.variants.map(v => v.price)).toFixed(2)}`
                              : `€${(product.price ?? 0).toFixed(2)}`
                            }
                          </p>
                        </div>
                      </div>
                      {/* Desktop text area */}
                      <div className="hidden md:block p-3 md:p-4">
                        <h3 className="text-sm md:text-lg font-semibold text-black dark:text-white mb-1 md:mb-2">
                          {product.name}
                        </h3>
                        <p className="text-sm md:text-base text-black dark:text-white">
                          {product.variants && product.variants.length > 0
                            ? `${t('product.from')} €${Math.min(...product.variants.map(v => v.price)).toFixed(2)}`
                            : `€${(product.price ?? 0).toFixed(2)}`
                          }
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-dark dark:text-white hover:bg-brand-blue hover:text-white'
                  }`}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`w-8 h-8 rounded-lg ${
                      currentPage === index + 1
                        ? 'bg-brand-blue text-white'
                        : 'text-dark dark:text-white hover:bg-brand-blue hover:text-white'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-dark dark:text-white hover:bg-brand-blue hover:text-white'
                  }`}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Shop;